
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import {TakipEntity} from "@/entity/TakipEntity";
import {BankaHesabiSahibiLabel} from "@/enum/BankaHesabiSahibi";

import TakipTalebiGenerator from "@/classes/evrak/TakipTalebi/TakipTalebiGenerator";
import FormWrapper from "@/components/FormWrapper.vue";
import Evrak from "@/classes/evrak/Evrak";
import {AlacakKalemiAlani} from "@/enum/AlacakKalemiAlani";
import KapakHesabi from "@/classes/evrak/KapakHesabi/KapakHesabi";
import KapakHesabiCalculator from "@/classes/evrak/KapakHesabi/KapakHesabiCalculator";
import {TakipDurumuLabel, TakipDurumu} from "@/enum/TakipDurumu";
import {HarcTuru} from "@/enum/HarcTuru";
import {ParaBirimi} from "@/enum/ParaBirimi";
import {TahsilatTuru} from "@/enum/TahsilatTuru";

@Component({
  components: {FormWrapper},
})
export default class OdemeTaahhuduTaslak extends Vue {
  @Prop() takip!: TakipEntity;
  @Prop() isKartNoVisible!: boolean;

  kapakHesabi: KapakHesabi = new KapakHesabi();
  ttEvrak: Evrak = new Evrak();
  bankaHesabiSahibiLabel = BankaHesabiSahibiLabel;
  alacakKalemiAlani = AlacakKalemiAlani;
  takipDurumu = TakipDurumuLabel;
  harcTuru = HarcTuru
  odemeTaksitDetaylari:any = []
  isLoadingEvrakData: boolean = false;
  isInternalUpdate: boolean = false;
  ParaBirimi = ParaBirimi
  TahsilatTuru = TahsilatTuru

  hesapAyar = JSON.parse(this.takip.taksit_hesap_ayar as unknown as string)

  get baroVekaletPuluGoster(): boolean {
    return this.hesapAyar?.baro_vekalet_pulu_gosterilsin_mi !== false
  }
  get vekaletSuretHarciGoster(): boolean {
    return this.hesapAyar?.vekalet_suret_harci_gosterilsin_mi !== false
  }
  get pesinHarcGoster(): boolean {
    return this.hesapAyar?.pesin_harc_gosterilsin_mi !== false
  }

  // TDT-3106: Peşin harç zaten Tahsil Harcı'nın içinde yer aldığından kalem listesinde AYRI
  // bir satır olarak HİÇBİR ZAMAN gösterilmez (elle toplanınca sistemin toplamıyla uyuşmuyordu).
  // Diğer harç/pul kalemleri ise yalnızca ilgili "gösterilsin mi?" ayarı kapalıyken gizlenir.
  kalemGorunsunMu(kalem: any): boolean {
    if (kalem['harc_turu'] === 'Peşin Harç') return false
    if (!this.vekaletSuretHarciGoster && kalem['harc_turu'] === 'Vekalet Suret Harcı') return false
    if (!this.baroVekaletPuluGoster && kalem['masraf_cesidi'] === 'Baro Vekalet Pulu') return false
    return true
  }

  // TDT-3079: Bir kalemin taahhüt toplamından (Genel Toplam / Kalan) düşülüp düşülmeyeceği.
  // Görünürlükten BAĞIMSIZDIR: peşin harç satırı her zaman gizli olsa da, toplamdan yalnızca
  // "peşin harç gösterilsin mi?" KAPALIYKEN düşülür. Varsayılanda (açık) peşin harç tahsil
  // harcının içinde kalır ve toplamdan düşülmez.
  kalemToplamdanDusulsunMu(kalem: any): boolean {
    if (!this.pesinHarcGoster && kalem['harc_turu'] === 'Peşin Harç') return true
    if (!this.vekaletSuretHarciGoster && kalem['harc_turu'] === 'Vekalet Suret Harcı') return true
    if (!this.baroVekaletPuluGoster && kalem['masraf_cesidi'] === 'Baro Vekalet Pulu') return true
    return false
  }

  // TDT-3079: Peşin harç gösterilmiyorsa Tahsil Harcı tutarı, peşin harç düşülmüş olarak yazdırılır.
  // Negatif tutar oluşmaması için 0 ile alt sınırlanır.
  kalemTutariStr(kalem: any): string {
    if (kalem['harc_turu'] === 'Tahsil Harcı' && !this.pesinHarcGoster) {
      return Math.max((Number(kalem['tutar']) || 0) - this.pesinHarc, 0).toFixed(2)
    }
    return kalem['tutar']
  }

  get gizlenenKalemlerToplami(): number {
    let toplam = 0
    this.kapakHesabi.duzenlenmisTakipSonrasiKalemler.forEach((kalem: any) => {
      if (this.kalemToplamdanDusulsunMu(kalem)) {
        // Her kalem KENDİ tutarıyla bir kez düşülür. Çok-alacaklı dosyada birden fazla
        // peşin harç satırı olabilir; her biri kendi tutarını katar (tek bir scalar değil).
        toplam += Number(kalem['tutar']) || 0
      }
    })
    return toplam
  }

  // TDT-3105: Takip sonrası işlemiş faiz kademeli (birden fazla alani=17 kalemi) olabilir.
  // Taahhütte her kademeyi ayrı yazmak yerine PARA BİRİMİ bazında toplam işlemiş faizi
  // tek satırda göstermek için. (Döviz takiplerde kademeler kursuz toplanmaz, ayrı satır.)
  get takipSonrasiIslemisFaizSatirlari(): Array<{ paraBirimi: string, toplam: number }> {
    const map = new Map<string, number>()
    this.kapakHesabi.duzenlenmisTakipSonrasiKalemler.forEach((kalem: any) => {
      if (Number(kalem['id']) === this.alacakKalemiAlani.takipSonrasiFaiz) {
        const pb = kalem['paraBirimi'] || 'TL'
        map.set(pb, (map.get(pb) || 0) + (Number(kalem['tutar']) || 0))
      }
    })
    return Array.from(map.entries()).map(([paraBirimi, toplam]) => ({ paraBirimi, toplam }))
  }

  // TDT-3107: Taahhüdü hangi borçlu(lar)ın verdiği taksit_hesap_ayar içinde seçilebilir.
  // Seçim yoksa (boş) tüm borçlular gösterilir; dosyada tek borçlu varsa zaten o gösterilir.
  get secilenBorcluIdleri(): Array<number> {
    const ids = this.hesapAyar?.secilen_borclu_idleri
    return Array.isArray(ids) ? ids.map((i: any) => Number(i)) : []
  }

  borcluSecili(borcluData: any): boolean {
    if (!this.secilenBorcluIdleri.length) return true
    return this.secilenBorcluIdleri.includes(Number(borcluData?.id))
  }

  headers = [
    {text: 'Taksit No', value: 'index'},
    {text: 'Tarih', value: 'tarih'},
    {text: 'Gün Sayısı', value: 'faiz_isleyen_gun'},
    {text: 'Faiz İşletilecek Tutar', value: 'faiz_isletilecek_tutar'},
    {text: 'İşleyecek Faiz', value: 'isleyen_faiz'},
    {text: 'Toplam Faiz', value: 'toplam_faiz'},
    {text: 'Toplam Borç', value: 'kalan_borc'},
    {text: 'Taksit Miktarı', value: 'tutar'},
    {text: 'Kalan', value: 'kalan_ana_para'},
  ]

  async handleTaksitler(){
    const takipId = this.takip?.id || this.$route.params.id;
    if (takipId) {
      try {
        const response = await this.$http.get('/api/v1/takip/' + takipId + '/taksit');
        this.odemeTaksitDetaylari = response.data;
      } catch (error) {
        console.error('Taksit verileri yüklenirken hata oluştu:', error);
      }
    }
  }
  get toplamFaiz(){
    let toplam:number = 0.00
    this.odemeTaksitDetaylari.forEach((taksit:any) => {
      toplam=toplam+Number(taksit.toplam_faiz)
    })
    return toplam.toFixed(2)
  }

  get taksitToplamFaizIsleyenGun(){
    let toplam:number = 0
    this.odemeTaksitDetaylari.forEach((taksit:any) => {
      toplam = toplam+taksit.faiz_isleyen_gun
    })
    return toplam
  }

  get pesinHarc(){
    let pesinHarc:number = 0
    // Peşin harç tutarını doğrudan alacak kalemlerinden al (Harç butonundaki tablo ile aynı kaynak)
    if (this.takip && this.takip.talepler) {
      this.takip.talepler.forEach((talep: any) => {
        if (talep.alacaklar) {
          talep.alacaklar.forEach((alacak: any) => {
            if (alacak.alacak_kalemleri) {
              alacak.alacak_kalemleri.forEach((kalem: any) => {
                if (kalem.harc_turu_id === this.harcTuru.pesin) {
                  // Çok-alacaklı dosyada birden fazla peşin harç kalemi olabilir; topla.
                  pesinHarc += Number(kalem.tutar)
                }
              })
            }
          })
        }
      })
    }
    return pesinHarc
  }
  get toplamIsleyecekFaiz(){
    let toplam:number = 0.00
    this.odemeTaksitDetaylari.forEach((taksit:any) => {
      toplam=toplam+Number(taksit.isleyen_faiz)
    })
    return toplam.toFixed(2)
  }

  get toplamtaksitMiktari(){
    let toplam:number = 0.00
    this.odemeTaksitDetaylari.forEach((taksit:any) => {
      toplam=toplam+Number(taksit.tutar)
    })
    return toplam.toFixed(2)
  }

  async mounted() {
    if (this.takip && this.takip.id) {
      this.isLoadingEvrakData = true;

      try {
        /*// Takip durumuna göre hangi store kullanılacağına karar ver
        const isAcilmamis = this.takip?.takip_durumu === TakipDurumu.acilmamis;

        // İlgili store'dan borçlu verilerini al
        const borclular = isAcilmamis
          ? this.$store.state.takipEkle?.takipBorclular?.data
          : this.$store.state.takipDetay?.takip?.borclular;

        if (borclular && Array.isArray(borclular) && borclular.length > 0) {
          // Takip durumuna göre ilgili store'un action'ını çağır
          const storeName = isAcilmamis ? 'takipEkle' : 'takipDetay';
          await this.$store.dispatch(`${storeName}/loadBorcluVekillerVeTebligatlar`, borclular);

          // State'deki güncel borçlu verilerini takip objesine merge et
          this.mergeBorcluDataFromState();
        }*/

        // Tüm hesaplamaları yap
        this.generateTtEvrak();
        this.calculateKapakHesabi();
        await this.handleTaksitler();
      } catch (error) {
        console.error('Ödeme taahhüdü verileri yüklenirken hata oluştu:', error);
      } finally {
        this.isLoadingEvrakData = false;
      }
    }
  }

  /*mergeBorcluDataFromState() {
    // Takip durumuna göre hangi store'dan veri çekileceğine karar ver
    const isAcilmamis = this.takip?.takip_durumu === TakipDurumu.acilmamis;

    // İlgili store'dan güncellenmiş borçlu verilerini al
    const stateBorclular = isAcilmamis
      ? this.$store.state.takipEkle?.takipBorclular?.data
      : this.$store.state.takipDetay?.takip?.borclular;

    if (!stateBorclular || !this.takip?.borclular) return;

    this.takip.borclular.forEach((takipBorclu: any) => {
      const stateBorclu = stateBorclular.find((sb: any) => sb.id === takipBorclu.id);
      if (stateBorclu) {
        // State'deki yeni field'ları takip objesindeki borçluya ekle
        takipBorclu.vekiller = stateBorclu.vekiller || [];
        takipBorclu.tebligatlar = stateBorclu.tebligatlar || [];
        takipBorclu.borclu_bilgileri = stateBorclu.borclu_bilgileri || null;
        takipBorclu.adresler = stateBorclu.adresler || [];
        takipBorclu.varsayilan_adres = stateBorclu.varsayilan_adres || null;
      }
    });
  }*/

  generateTtEvrak() {
    if (this.takip && this.takip.id) {
      try {
        this.isInternalUpdate = true;

        // Borçlular için eksik alanları kontrol et ve varsayılan değerler ekle
        if (this.takip.borclular && this.takip.borclular.length > 0) {
          this.takip.borclular.forEach((borclu: any) => {
            // borclu_bilgileri yoksa boş obje oluştur
            if (!borclu.borclu_bilgileri) {
              borclu.borclu_bilgileri = {
                yabanci_uyruklu_mu: false,
                tebligat_adresi: null,
                vekiller: [],
                adresler: []
              };
            }
            // yabanci_uyruklu_mu yoksa false yap
            if (borclu.borclu_bilgileri.yabanci_uyruklu_mu === undefined) {
              borclu.borclu_bilgileri.yabanci_uyruklu_mu = false;
            }
          });
        }

        let takipTalebiGenerator = new TakipTalebiGenerator()
        takipTalebiGenerator.setTakip(this.takip);
        this.ttEvrak = takipTalebiGenerator.getTakipTalebiEvrak();

      } catch (error) {
        console.error('TakipTalebiGenerator hatası:', error);
        // Hata durumunda en azından temel bilgileri set et
        this.ttEvrak.icraDairesi = this.takip.icra_dairesi?.isim || '';
        this.ttEvrak.dosyaEsasNo = this.takip.dosya_no || '';
        this.ttEvrak.kartNo = this.takip.id?.toString() || '';
      } finally {
        // Reset flag after mutations complete
        this.$nextTick(() => {
          this.isInternalUpdate = false;
        });
      }
    }
  }

  get dovizTahsilatlar(){
    let tahsilatlar :any = []
    let dovizToplami:number = 0

    this.takip.tahsilatlar.forEach(tah =>{
      tahsilatlar.push(tah)
    })
    tahsilatlar = tahsilatlar.filter((tah:any) => tah.para_birimi_id !== ParaBirimi.TRY);

    tahsilatlar.forEach((doviz:any) => {
      dovizToplami += doviz.tutar * doviz.kur;
    });
    return dovizToplami;
  }

  get tahsilHarcOrani(){
    let tahsilHarcOrani:any
    this.takip.talepler[0].alacaklar.forEach((alacak) => {
      alacak.alacak_kalemleri.forEach((kalemler) => {
        if (kalemler.harc_turu_id === this.harcTuru.tahsil_harci) {
          tahsilHarcOrani=kalemler.harc_orani;
        }
      })
    })
    if (tahsilHarcOrani==null){
      tahsilHarcOrani = "4.55"
    }
    return tahsilHarcOrani

  }

  get cezaEviHarci(){
    let tahsilatlar:any = []

    this.takip.tahsilatlar.forEach((tahsilat:any)=>{
      tahsilatlar ={"cezaevi_harci":tahsilat.cezaevi_harci,
        "tahsilat_turu": tahsilat.tahsilat_turu}
    })
    return tahsilatlar
  }

  calculateKapakHesabi() {
    if (this.takip.id) {
      let kapakHesabiCalculator: KapakHesabiCalculator = new KapakHesabiCalculator();
      kapakHesabiCalculator.setTakip(this.takip);
      this.kapakHesabi = kapakHesabiCalculator.calculateKapakHesabi();
      this.kapakHesabi.alacakKalemiAlanlari.sort(function (a, b) {
        return a - b;
      });
      this.kapakHesabi.takipSonrasialacakKalemiAlanlari.sort(function (a, b) {
        return a - b;
      });
    }
  }

  @Watch("takip", { deep: true })
  onTakipChange() {
    // Prevent infinite loop from internal updates
    if (this.isInternalUpdate) {
      return;
    }

    if (this.takip && this.takip.id) {
      this.generateTtEvrak();
    }
  }

  print() {
    this.$helper.printComponent('odemeTaahhuduTaslak');
  }

  imzasizUdfIndir(){
    this.$helper.getUdfFromComponent("odemeTaahhuduTaslak",true,this.takip.id + "_odemeTaahhuduTaslak");
  }

  imzaliUdfIndir(){
    this.$helper.getImzaliUdfFromComponent("odemeTaahhuduTaslak",true,this.takip.id + "_imzali_odemeTaahhuduTaslak");
  }

}
